import {
    type ContactInfo,
    type RealRewardProps,
    RealRewardStep,
} from "@hg/activity-utils"
import cls from "classnames"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { ModalContent } from "@/components/Modal"
import StrokeText from "@/components/StrokeText"
import Toast from "@/components/Toast"
import { i18n } from "@/i18n"
import { RuleModal } from "./Rule"

import styles from "./index.module.scss"

// 日服收货表单在 lib 的 ContactInfo 基础上扩展 email / postcode 字段。
// address1（住所1）存储在 ContactInfo 的 province 字段
// address2（住所2）存储在 ContactInfo 的 address 字段
type UsContact = ContactInfo & {
    email?: string
    postcode?: string
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const PHONE_RE = /^\d{10,11}$/
const POSTCODE_RE = /^\d{7}$/

// ─── フィールド定義（EDIT / CONFIRM 共用） ─────────────────────────────────────

type FieldDef = {
    key: keyof UsContact
    label: string
    readOnly?: boolean
    placeholder?: string
}

export const RealReward: React.FC<RealRewardProps> = (props) => {
    const {
        reward,
        step,
        loading,
        contact,
        onContactChange,
        onStepChange,
        onSubmitContact,
        onClose,
    } = props

    const c = contact as UsContact

    const [fetchingAddress, setFetchingAddress] = useState(false)

    // 用 ref 持有最新 contact，避免异步回调中的 stale closure
    const contactRef = useRef(contact)
    useEffect(() => {
        contactRef.current = contact
    }, [contact])

    // ─── 电话：过滤非数字，最多11位 ───────────────────────────────────────────
    const handlePhoneChange = useCallback(
        (value: string) => {
            const digits = value.replace(/\D/g, "").slice(0, 11)
            onContactChange({ ...contactRef.current, phone: digits } as ContactInfo)
        },
        [onContactChange],
    )

    // ─── 邮编：过滤非数字，最多7位，7位时自动查询住所 ──────────────────────────
    const handlePostcodeChange = useCallback(
        async (value: string) => {
            const digits = value.replace(/\D/g, "").slice(0, 7)
            // 先清空 province（address1）并更新 postcode
            onContactChange({
                ...contactRef.current,
                postcode: digits,
                province: "",
            } as ContactInfo)
            if (digits.length !== 7) return

            setFetchingAddress(true)
            try {
                const res = await fetch(
                    `https://api.zipaddress.net/?zipcode=${digits}`,
                )
                const json = await res.json()
                const fullAddress =
                    json.code === 200 ? (json.data?.fullAddress ?? "") : ""
                if (fullAddress) {
                    // 用 contactRef.current 取最新状态（包含用户在等待期间的其他输入）
                    onContactChange({
                        ...contactRef.current,
                        province: fullAddress,
                    } as ContactInfo)
                } else {
                    Toast.message(i18n.getRecall("message.address1_fetch_failed"))
                }
            } catch {
                Toast.message(i18n.getRecall("message.address1_fetch_failed"))
            } finally {
                setFetchingAddress(false)
            }
        },
        [onContactChange],
    )

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            onContactChange({
                ...contact,
                [e.target.name]: e.target.value,
            } as ContactInfo)
        },
        [contact, onContactChange],
    )

    const validate = useCallback((): string | null => {
        if (!c.name) return i18n.getRecall("message.name_empty")
        if (!c.phone) return i18n.getRecall("message.phone_empty")
        if (!PHONE_RE.test(c.phone))
            return i18n.getRecall("message.phone_invalid")
        if (!c.email) return i18n.getRecall("message.email_empty")
        if (!EMAIL_RE.test(c.email))
            return i18n.getRecall("message.email_invalid")
        if (!c.postcode) return i18n.getRecall("message.postcode_empty")
        if (!POSTCODE_RE.test(c.postcode))
            return i18n.getRecall("message.postcode_invalid")
        if (!c.province) return i18n.getRecall("message.address1_empty")
        if (!c.address) return i18n.getRecall("message.address_empty")
        return null
    }, [c])

    // ─── フィールド定義 ──────────────────────────────────────────────────────
    const FIELDS = useMemo((): FieldDef[] => [
        {
            key: "name",
            label: i18n.get("lottery.placeholder_name"),
        },
        {
            key: "phone",
            label: i18n.get("lottery.placeholder_phone"),
        },
        {
            key: "email",
            label: i18n.get("lottery.placeholder_email"),
        },
        {
            key: "postcode",
            label: i18n.get("lottery.placeholder_postcode"),
        },
        {
            key: "province",
            label: i18n.get("lottery.placeholder_address1"),
            readOnly: true,
            placeholder: fetchingAddress
                ? i18n.get("lottery.placeholder_address1_loading")
                : i18n.get("lottery.placeholder_address1"),
        },
        {
            key: "address",
            label: i18n.get("lottery.placeholder_address2"),
        },
    ], [fetchingAddress])

    const isPreview = step === RealRewardStep.PREVIEW

    const showCancelBtn = step === RealRewardStep.CONFIRM

    const handleClickCancel = useCallback(() => {
        if (step === RealRewardStep.CONFIRM) {
            onStepChange(RealRewardStep.EDIT)
        }
    }, [step, onStepChange])

    const showOkBtn = ![RealRewardStep.DONE, RealRewardStep.PREVIEW].includes(step)

    const handleClickOk = useCallback(() => {
        if (loading) return

        if (step === RealRewardStep.CONFIRM) {
            onSubmitContact()
        } else if (step === RealRewardStep.EDIT) {
            const err = validate()
            if (err) {
                Toast.message(err)
                return
            }
            onStepChange(RealRewardStep.CONFIRM)
        }
    }, [step, onSubmitContact, onStepChange, loading, validate])

    const okText = step === RealRewardStep.CONFIRM
        ? i18n.get("common.ok")
        : i18n.get("common.next")

    const [ruleOpen, setRuleOpen] = useState(false)

    return (
        <ModalContent
            size="large"
            onCancel={onClose}
            cancelText={i18n.get("common.back")}
            cancelButtonProps={{ onClick: handleClickCancel }}
            showCancelBtn={showCancelBtn}
            okText={okText}
            onOk={handleClickOk}
            showOkBtn={showOkBtn}
        >
            <div className={styles.congratulations} />
            <div className={styles.realReward}>
                {[RealRewardStep.EDIT, RealRewardStep.PREVIEW].includes(step) ? (
                    <>
                        <div className={styles.goods}>
                            <div
                                className={styles.imgWrapper}
                                data-key={reward.key}
                            >
                                <img src={reward.src} alt={reward.title} />
                            </div>
                            <div className={styles.name}>
                                <StrokeText
                                    text={reward.title}
                                    fontFamily="source-han-sans-japanese"
                                    fontWeight={700}
                                    fontSize={26}
                                    fillColor="#fffbf1"
                                    borderWidth={4}
                                    borderColor="#614518"
                                    offsetY={0}
                                    padding={3}
                                />
                            </div>
                        </div>
                        <div className={styles.contact}>
                            {FIELDS.map(({ key, readOnly, placeholder, label }) => (
                                <input
                                    key={key}
                                    type="text"
                                    name={key}
                                    value={
                                        fetchingAddress && key === "province"
                                            ? ""
                                            : (c[key] as string) ?? ""
                                    }
                                    readOnly={isPreview || readOnly}
                                    style={
                                        key === "province" || key === "address"
                                            ? { width: "728px" }
                                            : undefined
                                    }
                                    inputMode={
                                        key === "phone" || key === "postcode"
                                            ? "numeric"
                                            : key === "email"
                                              ? "email"
                                              : undefined
                                    }
                                    placeholder={placeholder ?? label}
                                    onChange={
                                        isPreview || readOnly
                                            ? undefined
                                            : key === "phone"
                                              ? (e) => handlePhoneChange(e.target.value)
                                              : key === "postcode"
                                                ? (e) => handlePostcodeChange(e.target.value)
                                                : handleChange
                                    }
                                />
                            ))}
                        </div>
                    </>
                ) : null}

                {step === RealRewardStep.CONFIRM ? (
                    <>
                        <div className={styles.introduction}>
                            {i18n.get("lottery.text_introduction")}
                        </div>
                        <div className={styles.mention}>
                            {i18n.get("lottery.text_mention")}
                        </div>
                        <div className={styles.rows}>
                            {FIELDS.map(({ key, label }) => {
                                const value = (c[key] as string) ?? ""
                                const isAddrField = key === "province" || key === "address"
                                const smaller = isAddrField && value.length > 22
                                return (
                                    <div
                                        key={key}
                                        className={cls(
                                            styles.row,
                                            isAddrField && styles.addressRow,
                                        )}
                                    >
                                        <div className={cls(
                                            styles.label,
                                            key === "email" && styles.email,
                                            key === "address" && styles.address2,
                                        )}>
                                            {label}
                                        </div>
                                        <div className={cls(
                                            styles.value,
                                            isAddrField ? styles.address : styles.ellipsis,
                                            smaller && styles.smaller,
                                        )}>
                                            {isAddrField ? <div>{value}</div> : value}
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </>
                ) : null}

                {step === RealRewardStep.DONE && (
                    <>
                        <div className={styles.introduction}>
                            {i18n.get("lottery.text_introduction")}
                        </div>
                        <div className={styles.mention}>
                            {i18n.get("lottery.text_mention")}
                        </div>
                    </>
                )}
            </div>
            <button
                type="button"
                className={styles.ruleButton}
                onClick={() => setRuleOpen(true)}
            >
                <StrokeText
                    text={i18n.get("lottery.btn_rule")}
                    fontFamily="source-han-sans-japanese"
                    fontWeight={700}
                    fontSize={27}
                    fillColor="#ffffff"
                    borderWidth={4}
                    borderColor="#564426"
                    offsetY={0}
                    padding={4}
                />
            </button>
            <RuleModal
                open={ruleOpen}
                onConfirm={() => setRuleOpen(false)}
                onClose={() => setRuleOpen(false)}
                loading={false}
                showOkBtn={false}
            />
        </ModalContent>
    )
}
